import { styled } from "@mui/material/styles";
import { primaryColor } from "../../Theme";

// Wraps a single icon button in its own `position: relative` box. Needed
// because VoiceSearchPill's own defensive rule below (`.MuiButtonBase-root...
// { position: "static" }`) has higher specificity than a button's own `sx`,
// so it always wins and forces the button back to `position: static`. Any
// `position: absolute` child of that button (RecordingIndicator, the click
// ripple's ::after) would otherwise escape up to the pill itself instead of
// staying confined to the button.
export const MicButtonWrapper = styled("div")({
  position: "relative",
  display: "inline-flex",
});
// Same wrapper, used for the other icon buttons (Clear/Upload/Done/Send) —
// kept as a separate export name for readability at each call site.
export const IconButtonWrapper = MicButtonWrapper;

// Recording indicator animation
export const RecordingIndicator = styled("div")({
  position: "absolute",
  top: "-2px",
  right: "-2px",
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: "#ff4444",
  animation: "pulse 1.5s infinite",
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.2)",
      opacity: 0,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
});

// Additional outer wave for enhanced sounder effect with 5px zoom in/out
export const OuterWave = styled("div")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  backgroundColor: `${primaryColor}`,
  pointerEvents: "none",
  animation: "sounderPulse 1s infinite",
  "@keyframes sounderPulse": {
    "0%": {
      transform: "translate(-50%, -50%) scale(1)",
      opacity: 1,
    },
    "25%": {
      transform: "translate(-50%, -50%) scale(1.05)",
      opacity: 0.5,
    },
    "50%": {
      transform: "translate(-50%, -50%) scale(1.2)",
      opacity: 0.3,
    },
    "75%": {
      transform: "translate(-50%, -50%) scale(1.05)",
      opacity: 0.5,
    },
    "100%": {
      transform: "translate(-50%, -50%) scale(1)",
      opacity: 1,
    },
  },
});

// Background circle for mic icon to make it stand out
export const MicBackground = styled("div")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%) scale(0)",
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  backgroundColor: "#fff",
  pointerEvents: "none",
  zIndex: 0,
  animation: "micBackgroundAppear 0.3s ease-out forwards",
  "@keyframes micBackgroundAppear": {
    "0%": {
      transform: "translate(-50%, -50%) scale(0)",
      opacity: 0,
    },
    "100%": {
      transform: "translate(-50%, -50%) scale(1)",
      opacity: 1,
    },
  },
});

// Green blinking indicator for WebSocket connection status
export const ConnectionIndicator = styled("div")({
  position: "absolute",
  top: "8px",
  right: "8px",
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: "#4caf50",
  zIndex: 1000,
  animation: "connectionBlink 2s infinite",
  "@keyframes connectionBlink": {
    "0%": {
      opacity: 1,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.5,
      transform: "scale(0.9)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
});

// Overlays the text field's area while recording. Sits absolutely inside the
// same relative-positioned slot as the TextField so it never affects pill
// layout/width — only opacity (and pointer-events) toggle between idle/active.
export const VoiceWaveContainer = styled("div")({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "3px",
  opacity: 0,
  pointerEvents: "none",
  transition: "opacity 0.25s ease",
  "&.active": {
    opacity: 1,
  },
});

// Individual soundwave bar. Only ever animated via transform (scaleY), set
// directly on the DOM node from the analyser render loop for 60fps updates
// without routing every frame through React state.
export const VoiceWaveBar = styled("div")({
  width: "3px",
  height: "28px",
  borderRadius: "999px",
  transformOrigin: "center",
  willChange: "transform",
  transform: "scaleY(0.2)",
});

// Plain wrapper — intentionally NOT extending DRxInputStyle, whose global
// `& button.MuiIconButton-root { position:absolute; top:3; right:0 }` rule would
// stack the mic and send button on top of each other in the corner.
export const VoiceSearchInputContainer = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: "2px",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  padding: 0,
  width: "100%",
});

// Click ripple for the pill's icon buttons — a real element (not a CSS
// `:active` pseudo-element) so remounting it via a changing `key` always
// plays the full grow-then-fade cycle once, regardless of how long the
// button was actually pressed (a short tap and a long hold look identical).
export const RippleSpan = styled("span")({
  position: "absolute",
  inset: 0,
  zIndex: 0,
  borderRadius: "50%",
  pointerEvents: "none",
  opacity: 0,
  animation: "iconButtonRipple 0.6s ease-out forwards",
  "@keyframes iconButtonRipple": {
    "0%": {
      transform: "scale(0.3)",
      opacity: 0,
    },
    "40%": {
      transform: "scale(1)",
      opacity: 0.45,
    },
    "100%": {
      transform: "scale(1.15)",
      opacity: 0,
    },
  },
});

// Rounded "pill" wrapper for the mic + input + send row (matches DigitalRx reference).
// The IconButton reset is defensive: it keeps the mic/send in normal flow even if
// any ancestor still carries the DRxInputStyle absolute-positioning rule.
export const VoiceSearchPill = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: "2px",
  backgroundColor: "#ffffff",
  border: `1.5px solid ${primaryColor}`,
  borderRadius: "999px",
  padding: "5px 8px 5px 6px",
  width: "100%",
  boxSizing: "border-box",
  "& .MuiButtonBase-root.MuiIconButton-root": {
    position: "static",
    top: "auto",
    right: "auto",
    background: "transparent",
    borderRadius: "50%",
  },
  [theme.breakpoints.down("md")]: {
    padding: "4px 6px 4px 4px",
    gap: "1px",
  },
}));

